import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { parseSharedRoutineFromURL, importSharedRoutine, getRoutineStats, SharedRoutine } from '../utils/routineSharing';

const ImportRoutine: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [sharedRoutine, setSharedRoutine] = useState<SharedRoutine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [customName, setCustomName] = useState('');
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Detect if running in PWA
    const isInPWA = window.matchMedia('(display-mode: standalone)').matches ||
                    (window.navigator as any).standalone === true;
    setIsPWA(isInPWA);
    
    const dataParam = searchParams.get('data');
    
    if (!dataParam) {
      setError('No routine data found in URL');
      return;
    }

    try {
      const routine = parseSharedRoutineFromURL(dataParam);
      setSharedRoutine(routine);
      setCustomName(routine.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse routine');
    }
  }, [searchParams]);



  const handleImport = async () => {
    if (!sharedRoutine) return;

    setImporting(true);
    try {
      // Update routine name if user customized it
      const routineToImport = {
        ...sharedRoutine,
        name: customName || sharedRoutine.name
      };

      const routineId = await importSharedRoutine(routineToImport);
      
      // Navigate to the imported routine
      navigate(`/routines/${routineId}`, { 
        state: { imported: true } 
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import routine');
      setImporting(false);
    }
  };


  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-4xl mb-2">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Import Failed
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>

        <div className="text-center space-y-3">
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!sharedRoutine) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-4xl mb-2">📋</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Import Routine
          </h1>
          <p className="text-gray-600">
            Import a workout routine from a shared link
          </p>
        </div>

        <div className="text-center space-y-3">
          <p className="text-gray-600">
            To import a routine, you need a valid share URL
          </p>
          
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const stats = getRoutineStats(sharedRoutine);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="text-4xl mb-2">📋</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Import Routine
        </h1>
        <p className="text-gray-600">
          Someone shared a workout routine with you!
        </p>
      </div>

      {/* PWA Import Instructions for Browser Users */}
      {!isPWA && (
        <div className="bg-gradient-to-r from-emerald-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="text-center mb-4">
            <div className="text-3xl mb-2">📱</div>
            <h3 className="text-xl font-bold mb-2">Import in Your Workout App</h3>
            <p className="text-emerald-100 text-sm">
              Get this routine into your PWA with our simple copy & import flow!
            </p>
          </div>
          
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                alert('✅ URL copied to clipboard!\n\nNow:\n1. Open your Workout App from the home screen\n2. Tap the purple "📋 Import" button\n3. Allow clipboard access when prompted\n\nThe routine will be imported automatically!');
              } catch (err) {
                // Fallback for older browsers
                alert('Please copy this URL manually and open your Workout App:\n\n' + window.location.href);
              }
            }}
            className="w-full bg-white text-emerald-600 font-semibold py-4 px-6 rounded-lg hover:bg-emerald-50 transition-colors text-lg mb-4 shadow-lg"
          >
            📋 Copy URL for PWA Import
          </button>
          
          <div className="text-center">
            <div className="text-emerald-100 text-sm mb-2">
              After copying, open your Workout App and tap "📋 Import"
            </div>
            <div className="text-emerald-200 text-xs">
              ✨ Simple clipboard-based sharing that works with iOS permissions
            </div>
          </div>
        </div>
      )}


      {/* Routine Preview */}
      <div className="bg-white border rounded-lg p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Routine Name
          </label>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter a name for this routine"
          />
        </div>

        {sharedRoutine.description && (
          <div className="mb-4">
            <h3 className="font-medium text-gray-700 mb-1">Description</h3>
            <p className="text-gray-600 text-sm">{sharedRoutine.description}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center bg-blue-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-blue-600">{stats.trainingDays}</div>
            <div className="text-xs text-blue-700 font-medium">Training Days</div>
          </div>
          <div className="text-center bg-emerald-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-emerald-600">{stats.uniqueExercises}</div>
            <div className="text-xs text-emerald-700 font-medium">Exercises</div>
          </div>
          <div className="text-center bg-purple-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-purple-600">{stats.totalSets}</div>
            <div className="text-xs text-purple-700 font-medium">Total Sets</div>
          </div>
          <div className="text-center bg-orange-50 rounded-lg p-3">
            <div className="text-2xl font-bold text-orange-600">{stats.estimatedDuration}m</div>
            <div className="text-xs text-orange-700 font-medium">Est. Duration</div>
          </div>
        </div>

        {/* Training Days Preview */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">Training Days</h3>
          {sharedRoutine.trainingDays.map((day, dayIndex) => (
            <div key={dayIndex} className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">{day.name}</h4>
              <div className="space-y-2">
                {day.exercises.slice(0, 5).map((exercise, exerciseIndex) => (
                  <div key={exerciseIndex} className="flex justify-between items-center text-sm">
                    <span className="text-gray-700">{exercise.name}</span>
                    <span className="text-gray-500">
                      {exercise.sets.length} sets × {exercise.sets.map(s => s.targetReps).join('-')} reps
                    </span>
                  </div>
                ))}
                {day.exercises.length > 5 && (
                  <div className="text-sm text-gray-500 italic">
                    +{day.exercises.length - 5} more exercises...
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Important Notes */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-medium text-yellow-800 mb-2">📝 Important Notes</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• All exercises will start in <strong>discovery mode</strong> - you'll set your own weights</li>
            <li>• The routine will be imported as <strong>inactive</strong> - activate it when ready</li>
            <li>• You can modify exercises and weights after importing</li>
          </ul>
        </div>

        {/* Metadata */}
        {sharedRoutine.metadata.sharedAt && (
          <div className="mt-4 text-xs text-gray-500 text-center">
            Shared on {new Date(sharedRoutine.metadata.sharedAt).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate('/')}
          className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors shadow-md"
        >
          Cancel
        </button>
        <button
          onClick={handleImport}
          disabled={importing || !customName.trim()}
          className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
        >
          {importing ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Importing...
            </span>
          ) : (
            `✅ ${isPWA ? 'Import Routine' : 'Import in Browser'}`
          )}
        </button>
      </div>

      {/* Browser Import Warning */}
      {!isPWA && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm text-center">
            ⚠️ <strong>Note:</strong> Importing in browser won't sync with your Workout App data
          </p>
        </div>
      )}

    </div>
  );
};

export default ImportRoutine;